import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';
// eslint-disable-next-line import/no-nodejs-modules
import { useParams, useHistory } from 'react-router-dom';
import moment from 'moment';
import entityTypes from 'js/enums/entity-types.enum';
import {
    api,
    deepCopy,
    getEventFilterForReportType,
    getEventTagSourceOrDefault,
    getPreset,
    setDocumentTitle,
    NotFoundError,
} from 'js/utils';
import Crumb from 'js/components/breadcrumbs/crumb';
import Layout from 'js/components/layout/layout';
import Navigation from 'js/components/navigation/navigation';
import TopBar from 'js/components/top-bar/top-bar';
import templateMethodMap from 'js/enums/templateMethodMap.enum';
import { EVENT_LOG_REPORT_MIN_DAYS_RANGE, MIN_DAYS_RANGE } from './constants';
import ReportTemplateDetail from './report-template-detail';

const initialTemplate = {
    name: '',
    advertiser: null,
    context: null,
    source: '',
    campaigns: [],
    schedule: null,
    start_date: null,
    end_date: null,
    type: entityTypes.DISPLAY_REPORTS,
};

const templateLinkMap = {
    [entityTypes.DISPLAY_REPORTS]: 'display',
    [entityTypes.VIDEO_REPORTS]: 'video',
    [entityTypes.EVENT_LOG_REPORTS]: 'event-log',
};

const retrieveTemplateData = async (templateId) => {
    let templateData;
    const fields = [
        'id',
        'name',
        'advertiser',
        'context',
        'schedule',
        'start_date',
        'end_date',
    ];

    try {
        templateData = await api().optimizeReportTemplates.retrieve(
            templateId,
            { field: fields },
        );
        templateData.type = entityTypes.DISPLAY_REPORTS;
    } catch (err) {
        try {
            if (!(err instanceof NotFoundError)) {
                throw err;
            }
            templateData = await api().videoReportTemplates.retrieve(
                templateId,
                { field: fields },
            );
            templateData.type = entityTypes.VIDEO_REPORTS;
        } catch (e) {
            if (!(e instanceof NotFoundError)) {
                throw e;
            }
            templateData = await api().eventLogReportTemplates.retrieve(
                templateId,
                { field: fields },
            );
            templateData.type = entityTypes.EVENT_LOG_REPORTS;
        }
    }

    return templateData;
};

export default function ReportTemplateDetailPage() {
    const { advertiserId: advertiserIdParam, templateId: templateIdParam } =
        useParams();
    const presetTemplate = getPreset();
    const [initialTemplateName, setInitialTemplateName] = useState('');
    const isNew = !templateIdParam;
    const history = useHistory();
    const [isLoading, setLoading] = useState(true);
    const [hasLoadingError, setHasLoadingError] = useState(false);
    const [template, setTemplate] = useState({
        ...deepCopy(initialTemplate),
        ...presetTemplate,
    });
    const [eventHierarchy, setEventHierarchy] = useState([]);
    const [isSaving, setSaving] = useState(false);
    const [savingErrors, setSavingErrors] = useState({});
    const [advertisers, setAdvertisers] = useState([]);
    const [contexts, setContexts] = useState([]);
    const [amending, setAmending] = useState({});
    const [minDaysRange, setMinDaysRange] = useState(MIN_DAYS_RANGE);

    const getBreadcrumbs = () => {
        const breadcrumbs = [
            <Crumb key="home" to="/" label="4D" />,
            <Crumb key="pulse" to="/pulse/" label="Pulse" />,
        ];
        if (hasLoadingError) {
            breadcrumbs.push(<Crumb key="error" hasError />);
        } else if (isLoading) {
            breadcrumbs.push(<Crumb key="loading" isLoading />);
        } else if (isNew) {
            breadcrumbs.push(<Crumb key="report" label="Create Report" />);
        } else {
            breadcrumbs.push(
                <Crumb
                    key="report"
                    label={initialTemplateName}
                    to={`/pulse/${
                        templateLinkMap[template.type]
                    }/${templateIdParam}/`}
                    isCollapsible
                />,
            );
            breadcrumbs.push(<Crumb key="edit" label="Edit" />);
        }

        return breadcrumbs;
    };

    const loadWizardData = useCallback(async () => {
        setLoading(true);
        setHasLoadingError(false);

        try {
            const callbacks = [
                api().advertisers.list({ ordering: 'name' }),
                api().contexts.list({
                    ordering: '-updated',
                    is_public: false,
                    verbose: true,
                    is_app: false, // insights for app contexts not supported
                }),
                api().eventHierarchy.list({ verbose: true }),
            ];
            const [
                { results: advertisersData },
                { results: contextsData },
                { results: eventHierarchyData },
            ] = await Promise.all(callbacks);

            eventHierarchyData.sort(
                (a, b) =>
                    getEventTagSourceOrDefault(a.source).localeCompare(
                        getEventTagSourceOrDefault(b.source),
                    ) ||
                    a.campaign.localeCompare(b.campaign) ||
                    a.line_item.localeCompare(b.line_item),
            );

            const hasDisplayData = eventHierarchyData.some(
                (event) => event.is_video === false,
            );
            const type = hasDisplayData
                ? entityTypes.DISPLAY_REPORTS
                : entityTypes.VIDEO_REPORTS;

            const validContexts = contextsData.filter(
                (item) => item.advertiser, // TODO: fix broken contexts and drop this workaround (see AV-3418)
            );
            const candidateContexts = validContexts.filter(
                ({ is_video: isVideo }) =>
                    type === entityTypes.EVENT_LOG_REPORTS ||
                    isVideo === (type === entityTypes.VIDEO_REPORTS),
            );
            const firstContext =
                candidateContexts.find(
                    ({ id }) => id === presetTemplate.context,
                ) ||
                candidateContexts.find(
                    ({ advertiser }) =>
                        advertiser === presetTemplate.advertiser,
                ) ||
                candidateContexts[0];
            const { source: firstSourceId, campaign: firstCampaignId } =
                eventHierarchyData.find(getEventFilterForReportType(type)) ||
                {};

            setAdvertisers(advertisersData);
            setContexts(validContexts);
            setEventHierarchy(eventHierarchyData);
            setTemplate((prevState) => ({
                ...prevState,
                advertiser: firstContext?.advertiser,
                context: firstContext?.id,
                source: firstSourceId,
                campaigns: [{ campaign_id: firstCampaignId, line_items: [] }],
                type,
            }));
        } catch (err) {
            setHasLoadingError(true);
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadTemplateData = useCallback(async () => {
        setLoading(true);
        setHasLoadingError(false);

        try {
            const unblock = history.block(
                ({ pathname }) => pathname !== '/errors/404/',
            );
            const templateData = await retrieveTemplateData(templateIdParam);
            unblock();
            if (templateData.advertiser !== advertiserIdParam) {
                throw new Error('invalid URL');
            }
            setTemplate(templateData);
            setInitialTemplateName(templateData.name);
        } catch (err) {
            setHasLoadingError(true);
        } finally {
            setLoading(false);
        }
    }, [advertiserIdParam, templateIdParam, history]);

    const isRangeValid = (startDate, endDate) =>
        moment(endDate).diff(moment(startDate), 'days') + 1 >= minDaysRange;

    const validateTemplate = ({
        fields = ['name', 'context', 'schedule'],
        throwErrors = false,
    } = {}) => {
        const amendingUpdates = {};
        const errors = { ...savingErrors };

        fields.forEach((field) => {
            amendingUpdates[field] = true;
            delete errors[field];

            if (field === 'name' && !template.name) {
                errors.name = 'A report name is required.';
            }

            if (field === 'context' && !template.context) {
                errors.context = 'A context to optimize is required.';
            }

            if (
                field === 'schedule' &&
                template.start_date &&
                template.end_date &&
                !isRangeValid(template.start_date, template.end_date)
            ) {
                errors.schedule = `Your custom dates must span at least ${minDaysRange} days.`;
            }

            if (field === 'file' && !template.file) {
                errors.file = 'A report file is required.';
            }
        });

        setAmending({ ...amending, ...amendingUpdates });
        setSavingErrors(errors);
        if (throwErrors && Object.keys(errors).length) {
            throw errors;
        }
    };

    const saveTemplate = async () => {
        setSavingErrors({});
        setSaving(true);

        try {
            validateTemplate({ throwErrors: true });
            if (isNew) {
                await api()[templateMethodMap[template.type]].create({
                    ...template,
                    file: null,
                });
            } else {
                await api()[templateMethodMap[template.type]].update(
                    template.id,
                    {
                        name: template.name,
                        schedule: template.schedule,
                        start_date: template.start_date,
                        end_date: template.end_date,
                    },
                );
            }
            await history.push(
                isNew
                    ? `/pulse/${template.advertiser}/`
                    : `/pulse/${templateLinkMap[template.type]}/${
                          template.id
                      }/`,
            );
        } catch (errors) {
            setSavingErrors(errors);
            setSaving(false);
        }
    };

    useLayoutEffect(() => {
        setSavingErrors({});

        if (isNew) {
            setAmending(false);
            setTemplate({ ...deepCopy(initialTemplate), ...presetTemplate });
            loadWizardData();
        } else {
            loadTemplateData();
            setAmending({ name: true });
        }

        return () => {
            api().abortAll();
        };
    }, [isNew, loadWizardData, loadTemplateData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isNew) {
            setDocumentTitle(['Create Report', 'Pulse']);
        } else if (initialTemplateName) {
            setDocumentTitle(['Edit', initialTemplateName, 'Pulse']);
        } else {
            setDocumentTitle(['Pulse']);
        }
    }, [isNew, initialTemplateName]);

    useEffect(() => {
        setSavingErrors((prevState) => {
            const newState = { ...prevState };
            delete newState.name;
            return newState;
        });

        if (amending.name) {
            validateTemplate({ fields: ['name'] });
        }
    }, [template.name]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSavingErrors((prevState) => {
            const newState = { ...prevState };
            delete newState.context;
            return newState;
        });
    }, [template.context]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (template.file) {
            setSavingErrors((prevState) => {
                const newState = { ...prevState };
                delete newState.file;
                return newState;
            });
        }

        if (amending.file) {
            validateTemplate({ fields: ['file'] });
        }
    }, [template.file]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSavingErrors((prevState) => {
            const newState = { ...prevState };
            delete newState.schedule;
            return newState;
        });

        if (amending.schedule) {
            validateTemplate({ fields: ['schedule'] });
        }
    }, [template.schedule, template.start_date, template.end_date]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isNew) return;

        setSavingErrors({});
        if (template.type === entityTypes.EVENT_LOG_REPORTS) {
            setMinDaysRange(EVENT_LOG_REPORT_MIN_DAYS_RANGE);
            setTemplate((prev) => ({
                ...prev,
                schedule: null,
                start_date: null,
                end_date: null,
            }));
        } else {
            setMinDaysRange(MIN_DAYS_RANGE);
        }
    }, [template.type]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        validateTemplate({ fields: ['schedule'] });
    }, [minDaysRange]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Layout
            header={<TopBar breadcrumbs={getBreadcrumbs()} />}
            sidebar={<Navigation />}
        >
            <ReportTemplateDetail
                template={template}
                eventHierarchy={eventHierarchy}
                advertisers={advertisers}
                contexts={contexts}
                isLoading={isLoading}
                hasLoadingError={hasLoadingError}
                isSaving={isSaving}
                savingErrors={savingErrors}
                onChange={setTemplate}
                onValidate={(fields) =>
                    validateTemplate({ fields, throwErrors: true })
                }
                onSave={saveTemplate}
                onReload={isNew ? loadWizardData : loadTemplateData}
                onFileUploadError={(err) => {
                    setTemplate((prev) => ({ ...prev, file: null }));
                    setSavingErrors((prev) => ({ ...prev, file: err }));
                }}
            />
        </Layout>
    );
}
